/**
 * Regole pure dell'editor del modello prestativo (domini, scala, tabelle).
 *
 * Erano dentro `AdminDashboard.js`; con la sezione coach «Sport e modelli» lo
 * stesso editor serve all'admin (modello di sistema) e ai coach (modello
 * personale), quindi le regole stanno qui, una volta sola, e si testano da sole.
 * Spec: docs/sport-config/ANALYSIS.md §4.5
 */

export const DEFAULT_SCORE_COLUMNS = [1, 5, 10, 15];
export const DEFAULT_SCORE_COLUMNS_TEXT = "1, 5, 10, 15";
export const MIN_SCORE_COLUMNS = 2;

/** Le unita' di un benchmark. Le etichette sono chiavi i18n. */
export const PERFORMANCE_UNITS = [
	{ value: "kg", labelKey: "pmUnitKg" },
	{ value: "reps", labelKey: "pmUnitReps" },
	{ value: "seconds", labelKey: "pmUnitSeconds" },
	{ value: "minutes", labelKey: "pmUnitMinutes" },
	{ value: "minutes_seconds", labelKey: "pmUnitMinSec" },
	{ value: "hit_factor", labelKey: "pmUnitHitFactor" },
	{ value: "meters", labelKey: "pmUnitMeters" },
	{ value: "points", labelKey: "pmUnitPoints" },
];

/** La scala di un dominio: numeri unici in ordine, mai vuota. */
export function normalizeScoreColumns(columns) {
	if (!Array.isArray(columns)) return [...DEFAULT_SCORE_COLUMNS];
	const values = Array.from(
		new Set(
			columns
				.map((value) => Number(value))
				.filter((value) => Number.isFinite(value)),
		),
	).sort((a, b) => a - b);
	return values.length > 0 ? values : [...DEFAULT_SCORE_COLUMNS];
}

/**
 * La scala scritta a mano: numerica per costruzione, perche' i valori sono i
 * punti di appoggio dell'interpolazione. Le etichette testuali (a, b, c) non
 * sono supportate e tornano in `invalid`.
 */
export function parseScoreColumnsInput(text) {
	const tokens = String(text || "")
		.split(/[\s,;]+/)
		.map((token) => token.trim())
		.filter(Boolean);
	const values = [];
	const invalid = [];
	for (const token of tokens) {
		const parsed = Number(token.replace(",", "."));
		if (Number.isFinite(parsed)) values.push(parsed);
		else invalid.push(token);
	}
	return {
		values: Array.from(new Set(values)).sort((a, b) => a - b),
		invalid,
	};
}

const refsOf = (value) => (Array.isArray(value) ? value : []);

function genderField(kind) {
	return kind === "female"
		? "scoring_references_female"
		: "scoring_references_male";
}

/**
 * Le tabelle per segmento: per division (il tiro, D4) e per categoria di peso
 * (pesistica e powerlifting, D6b). Stessa forma, `{ chiave: [riferimenti] }`.
 */
const SEGMENT_FIELDS = {
	division: "scoring_references_by_division",
	weight_class: "scoring_references_by_weight_class",
};

/**
 * Il riferimento di un esercizio per un livello, nella tabella scelta.
 *
 * `target` e' `{ kind: "male" | "female" }`, `{ kind: "division", key }` o
 * `{ kind: "weight_class", key, gender }`. Per genere si mostra la tabella
 * generale quando quella del genere e' vuota, come ha sempre fatto il pannello;
 * per un segmento no: una cella vuota vuol dire proprio «per questa division o
 * categoria vale la tabella per genere».
 */
export function getReferenceValue(exercise, score, target) {
	if (!exercise) return "";
	let refs;
	const segmentField = SEGMENT_FIELDS[target?.kind];
	if (segmentField) {
		refs = refsOf(exercise[segmentField]?.[target.key]);
	} else {
		const own = refsOf(exercise[genderField(target?.kind)]);
		refs = own.length > 0 ? own : refsOf(exercise.scoring_references);
	}
	const found = refs.find((ref) => Number(ref?.score) === Number(score));
	return found?.value ?? "";
}

/**
 * Il corpo della PUT che cambia un riferimento: `value` gia' convertito
 * nell'unita' dell'esercizio, oppure `null` per svuotare la cella.
 *
 * Per genere si parte dalla tabella del genere, o dalla generale se vuota, cosi'
 * la prima cella scritta non cancella le altre. Per division si scrive la sola
 * division, e una tabella che resta vuota sparisce dalla mappa.
 */
export function buildReferenceUpdate(exercise, score, value, target) {
	const without = (refs) =>
		refs.filter((ref) => Number(ref?.score) !== Number(score));
	const withValue = (refs) =>
		value === null || value === undefined
			? without(refs)
			: [...without(refs), { score: Number(score), value }];

	const segmentField = SEGMENT_FIELDS[target?.kind];
	if (segmentField) {
		const bySegment = { ...(exercise?.[segmentField] || {}) };
		const next = withValue(refsOf(bySegment[target.key]));
		if (next.length > 0) bySegment[target.key] = next;
		else delete bySegment[target.key];
		return { [segmentField]: bySegment };
	}

	const field = genderField(target?.kind);
	const own = refsOf(exercise?.[field]);
	const seed = own.length > 0 ? own : refsOf(exercise?.scoring_references);
	return { [field]: withValue(seed) };
}

/** Sposta un elemento su o giu' e restituisce il nuovo ordine (o null). */
export function moveItem(items, id, direction, getId = (item) => item.id) {
	const index = items.findIndex((item) => getId(item) === id);
	if (index === -1) return null;
	const next = direction === "up" ? index - 1 : index + 1;
	if (next < 0 || next >= items.length) return null;
	const reordered = [...items];
	const [moved] = reordered.splice(index, 1);
	reordered.splice(next, 0, moved);
	return reordered;
}

/**
 * Gli elementi di libreria proponibili in un dominio.
 *
 * Il modello di sistema accetta solo la libreria globale (D8 di
 * docs/sport-config/DECISIONS.md, imposto anche dal server); quello di un coach
 * anche la sua libreria personale.
 */
export function filterLibraryItems(
	items,
	{ usedIds = [], search = "", source = "all", category = "all", globalOnly = false } = {},
) {
	const used = new Set(usedIds);
	const query = String(search || "").toLowerCase();
	return (items || []).filter((item) => {
		if (used.has(item._sourceId)) return false;
		if (globalOnly && item._libraryScope !== "global") return false;
		if (query && !String(item.name || "").toLowerCase().includes(query))
			return false;
		if (source !== "all" && item._sourceType !== source) return false;
		if (
			category !== "all" &&
			String(item.category || "").toLowerCase() !== category.toLowerCase()
		)
			return false;
		return true;
	});
}

/** Esercizi e conditioning in una lista sola, nella forma del selettore. */
export function toLibraryItems(exercises, conditioning) {
	const exerciseItems = (exercises || []).map((e) => ({
		_sourceId: e.exercise_id,
		_sourceType: "exercise",
		name: e.name,
		category: e.category || "other",
		_libraryScope: e.library_scope === "personal" ? "personal" : "global",
	}));
	const conditioningItems = (conditioning || []).map((c) => ({
		_sourceId: c.conditioning_id,
		_sourceType: "conditioning",
		name: c.name,
		category: "conditioning",
		conditioningType: c.conditioning_type,
		_libraryScope: c.library_scope === "personal" ? "personal" : "global",
	}));
	return [...exerciseItems, ...conditioningItems];
}

/**
 * Categorie di peso pronte, da cui partire (D6b). Sono **suggerimenti**: quello
 * che vale e' l'elenco salvato sullo sport, che si modifica dopo. Verificati il
 * 2026-10-26: IWF senior e junior dal 1 agosto 2026 (decise il 3 novembre 2025:
 * sostituiscono le 71 79 88 94 maschili e le 48 58 63 femminili del giugno 2025;
 * alle Olimpiadi di Los Angeles solo M 65 75 85 95 110 +110 e F 53 61 69 77 86
 * +86), IPF open (dal 2021, con 69 e 76 femminili al posto
 * della 72). Le regole cambiano per ciclo olimpico: ricontrollarle quando si
 * aggiornano.
 */
export const WEIGHT_CLASS_PRESETS = [
	{
		id: "iwf_2026",
		labelKey: "wcPresetIwf",
		male: [60, 65, 70, 75, 85, 95, 110],
		female: [49, 53, 57, 61, 69, 77, 86],
	},
	{
		id: "ipf",
		labelKey: "wcPresetIpf",
		male: [59, 66, 74, 83, 93, 105, 120],
		female: [47, 52, 57, 63, 69, 76, 84],
	},
];

/** Le categorie di un preset, con la categoria aperta in fondo per genere. */
export function presetClasses(preset) {
	if (!preset) return [];
	const build = (gender, caps) => [
		...caps.map((max) => ({ gender, max_kg: max })),
		{ gender, max_kg: null },
	];
	return [...build("male", preset.male || []), ...build("female", preset.female || [])];
}

/** Le categorie di un genere, nell'ordine del server (la aperta in fondo). */
export function classesOfGender(classes, gender) {
	return (Array.isArray(classes) ? classes : []).filter(
		(item) => item?.gender === gender,
	);
}


// ── Il livello di un esercizio nel profilo ─────────────────────────────────

/** Le unita' in cui meno e' meglio: gemello di `_unit_higher_is_better`. */
const LOWER_IS_BETTER_UNITS = new Set(["seconds", "minutes", "minutes_seconds"]);

/** La chiave di una colonna nei nomi dei livelli: 1 -> "1", 2.5 -> "2.5". */
export const scoreKey = (score) => String(Number(score));

/** Il nome di una colonna della scala, o null. */
export function scoreLabelOf(labels, score) {
	if (!labels || score === null || score === undefined) return null;
	return labels[scoreKey(score)] || null;
}

/**
 * Dove sta un atleta sulla scala di un esercizio, per la barra del profilo.
 *
 * Dal server arrivano il punteggio interpolato (`score_value`), la tabella con
 * cui e' stato calcolato (`scoring_references`) e il nome del livello raggiunto
 * (`score_label`); qui si aggiungono la posizione sulla barra, le tacche e il
 * prossimo traguardo. `null` se l'esercizio non ha una tabella o un valore:
 * senza, un livello non c'e' e non va disegnato.
 *
 * `next` e' la prima colonna sopra il punteggio, con quanto manca: in piu' per
 * chili e ripetizioni, in meno per i tempi.
 */
export function levelProgress(exercise, domain) {
	const refs = (Array.isArray(exercise?.scoring_references) ? exercise.scoring_references : [])
		.map((ref) => ({ score: Number(ref?.score), value: Number(ref?.value) }))
		.filter((ref) => Number.isFinite(ref.score) && Number.isFinite(ref.value))
		.sort((a, b) => a.score - b.score);
	const score = Number(exercise?.score_value);
	const value = Number(exercise?.value);
	// `Number(null)` e' 0: un massimale assente diventerebbe un livello.
	const missing = (raw) => raw === null || raw === undefined || raw === "";
	if (
		refs.length < 2 ||
		missing(exercise?.score_value) ||
		missing(exercise?.value) ||
		!Number.isFinite(score) ||
		!Number.isFinite(value)
	) {
		return null;
	}
	const columns = normalizeScoreColumns(exercise?.scoring_columns || domain?.scoring_columns);
	const max = Math.max(columns[columns.length - 1], refs[refs.length - 1].score);
	const labels = domain?.scoring_labels || {};
	const lowerIsBetter = LOWER_IS_BETTER_UNITS.has(exercise?.unit);
	const nextRef = refs.find((ref) => ref.score > score + 1e-9) || null;
	const toPercent = (scoreValue) => Math.max(0, Math.min(100, (scoreValue / max) * 100));
	return {
		score,
		max,
		label: exercise?.score_label || null,
		percent: toPercent(score),
		ticks: columns.map((column) => ({
			score: column,
			percent: toPercent(column),
			reached: column <= score + 1e-9,
		})),
		next: nextRef
			? {
					score: nextRef.score,
					value: nextRef.value,
					label: scoreLabelOf(labels, nextRef.score),
					delta: lowerIsBetter ? value - nextRef.value : nextRef.value - value,
				}
			: null,
		lowerIsBetter,
	};
}
